import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

/**
 * ─── DataTable ─────────────────────────────────────────────────────────────
 * The ONLY table primitive in the Owner Console (PDR v2.1 §8).
 * Formalizes the .hbf-datatable pattern.
 *
 * Props
 *   columns   Array<{
 *               key: string,
 *               header: string,
 *               align?: "left"|"right"|"center",
 *               sortable?: boolean,
 *               sortAccessor?: (row) => primitive,
 *               render: (row) => ReactNode,
 *               width?: string|number,
 *             }>
 *   rows      Array<any>
 *   getRowId  (row) => string    — required for React key
 *   loading   boolean            — shows skeleton
 *   emptyTitle, emptyDescription — shown when rows.length === 0 and !loading
 *   onRowClick (row) => void     — makes rows keyboard-activatable
 *   testId
 *
 * Sorting
 *   Internal state. Click a sortable header to cycle none → asc → desc → none.
 *   Uses `sortAccessor(row)` when provided; else `row[column.key]`.
 *
 * Accessibility
 *   - <th aria-sort> reflects direction.
 *   - Sortable header is a button.
 *   - Optional row click wires role="button" + Enter/Space activation.
 */
export default function DataTable({
  columns, rows,
  getRowId = (r) => r.id,
  loading = false,
  emptyTitle = "No results",
  emptyDescription = "There are no records to display.",
  onRowClick,
  testId,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null); // "asc" | "desc"

  const toggleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey !== col.key)     { setSortKey(col.key); setSortDir("asc"); return; }
    if (sortDir === "asc")       { setSortDir("desc"); return; }
    setSortKey(null); setSortDir(null);
  };

  const sortedRows = useMemo(() => {
    if (!sortKey || !sortDir) return rows;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return rows;
    const acc = col.sortAccessor || ((r) => r[col.key]);
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = acc(a); const bv = acc(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, columns, sortKey, sortDir]);

  return (
    <div className="ds-datatable" data-testid={testId}>
      <div className="ds-datatable__scroll">
        <table>
          <thead>
            <tr>
              {columns.map(col => {
                const active = sortKey === col.key;
                const ariaSort = col.sortable
                  ? (active ? (sortDir === "asc" ? "ascending" : "descending") : "none")
                  : undefined;
                return (
                  <th
                    key={col.key}
                    style={{
                      textAlign: col.align || "left",
                      width: col.width,
                    }}
                    aria-sort={ariaSort}
                    onClick={col.sortable ? () => toggleSort(col) : undefined}
                    data-testid={testId ? `${testId}-th-${col.key}` : undefined}
                  >
                    {col.header}
                    {col.sortable && (
                      <span className="ds-datatable__sort-icon" aria-hidden>
                        {!active && <ChevronsUpDown size={11} strokeWidth={2.2} />}
                        {active && sortDir === "asc" && <ChevronUp size={11} strokeWidth={2.4} />}
                        {active && sortDir === "desc" && <ChevronDown size={11} strokeWidth={2.4} />}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <tr key={`skel-${i}`} aria-hidden data-testid={testId ? `${testId}-skel-${i}` : undefined}>
                {columns.map((c, idx) => (
                  <td key={c.key || idx} style={{ textAlign: c.align || "left" }}>
                    <span className="ds-datatable__skel" />
                  </td>
                ))}
              </tr>
            ))}
            {!loading && sortedRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="ds-datatable__empty">
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{emptyTitle}</div>
                  <div style={{ fontSize: 12.5 }}>{emptyDescription}</div>
                </td>
              </tr>
            )}
            {!loading && sortedRows.map(row => {
              const id = getRowId(row);
              const clickable = Boolean(onRowClick);
              return (
                <tr
                  key={id}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row); } } : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? "button" : undefined}
                  style={clickable ? { cursor: "pointer" } : undefined}
                  data-testid={testId ? `${testId}-row-${id}` : undefined}
                >
                  {columns.map(col => (
                    <td key={col.key} style={{ textAlign: col.align || "left" }}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
